import { useState, useMemo } from "react";
import { useGame } from "@/context/GameContext";
import { ITEM_ICON, itemName } from "@/lib/gameData";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export function BankPanel() {
  const { snap, content, bankDeposit, bankWithdraw, bankDepositAll } = useGame();
  const [filter, setFilter] = useState("");

  const bank = snap?.bank || [];
  const inv = snap?.inventory || [];

  const filtered = useMemo(() => {
    const q = filter.toLowerCase();
    return bank.filter((s) => itemName(s.item, content?.items).toLowerCase().includes(q));
  }, [bank, filter, content]);

  async function doWithdraw(id, qty) {
    try { await bankWithdraw(id, qty); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function doDeposit(id, qty) {
    try { await bankDeposit(id, qty); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function depositAll() {
    try { await bankDepositAll(); toast.success("Deposited all"); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="p-4 lg:p-6 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="bank-panel">
      <section className="panel p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display text-xl heading-gilded">Bank vault</h3>
            <div className="text-stone-500 text-xs font-numeric">{bank.length} / 400 stacks</div>
          </div>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search…"
            className="bg-stone-950 border border-stone-700 rounded px-2 py-1 text-sm w-40"
            data-testid="bank-filter"
          />
        </div>
        <div className="grid grid-cols-6 md:grid-cols-8 gap-1.5 max-h-[420px] overflow-y-auto scroll-thin pr-1">
          {filtered.length === 0 && (
            <div className="col-span-full text-stone-600 italic text-sm font-body py-6 text-center">
              Your vault is empty.
            </div>
          )}
          {filtered.map((s, i) => (
            <button
              key={`${s.item}-${i}`}
              className="inv-slot group"
              title={`${itemName(s.item, content?.items)} × ${s.qty}\nLeft-click: withdraw 1\nRight-click: withdraw all`}
              onClick={() => doWithdraw(s.item, 1)}
              onContextMenu={(e) => { e.preventDefault(); doWithdraw(s.item, s.qty); }}
              data-testid={`bank-slot-${s.item}`}
            >
              <span className="qty">{s.qty > 1 ? s.qty : ""}</span>
              <span className="text-2xl">{ITEM_ICON[s.item] || "◇"}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="panel p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display text-xl heading-gilded">Backpack</h3>
            <div className="text-stone-500 text-xs font-numeric">{inv.length} / 28 slots</div>
          </div>
          <button onClick={depositAll} className="btn-ghost-gold text-sm px-3 py-1" data-testid="deposit-all-btn">
            Deposit all
          </button>
        </div>
        <div className="grid grid-cols-4 gap-1.5 max-w-sm">
          {Array.from({ length: 28 }, (_, i) => inv[i] || null).map((s, i) => (
            <button
              key={i}
              className="inv-slot"
              disabled={!s}
              title={s ? `${itemName(s.item, content?.items)} × ${s.qty}\nLeft-click: deposit 1\nRight-click: deposit all` : "Empty"}
              onClick={() => s && doDeposit(s.item, 1)}
              onContextMenu={(e) => { e.preventDefault(); if (s) doDeposit(s.item, s.qty); }}
              data-testid={`bank-inv-${i}`}
            >
              {s && (
                <>
                  <span className="qty">{s.qty > 1 ? s.qty : ""}</span>
                  <span className="text-2xl">{ITEM_ICON[s.item] || "◇"}</span>
                </>
              )}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
